import { useCallback, useEffect, useRef, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { Coordinates } from '@/domain/protocols/Coordinates'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'

import { IObstacleRepository } from '@/data/ObstacleRepository/IObstacleRepository'

import { MapObstaclesLayer } from '../components/MapObstacles/MapObstaclesLayer'
import { getObstacleTypeToken } from '../components/MapObstacles/obstacleFormatters'
import {
  MIN_OBSTACLE_LABEL_ZOOM,
  MIN_OBSTACLE_ZOOM,
  OBSTACLE_CLUSTER_ZOOM_BUMP
} from '../components/MapObstacles/obstacleLayerSpecs'
import { useMapContext } from '../components/MapView/MapContext'
import { MapImperativeInterface } from '../interfaces/MapImperativeInterface'
import { FeatureInfoState, FeatureInfoStateMutator, ObstacleFeatureInfoData } from '../states/FeatureInfoState'
import { getFeatureFlyToPadding, ScreenElementsStateMutator } from '../states/ScreenElementsState'

import type { ILogger } from '@/infra/logger/ILogger'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'

function getActiveObstacleId(): string | undefined {
  const active = FeatureInfoState.getValue().active
  return active?.type === 'obstacle' ? active.obstacle.obstacleId : undefined
}

function MapObstaclesPresenter() {
  const [mapLayerState] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)
  const obstacleRepository = useInjection<IObstacleRepository>(InjectionTokens.ObstacleRepository)
  const logger = useInjection<ILogger>(InjectionTokens.Logger)
  const { t } = useI18n()
  const { map } = useMapContext()
  const requestIdRef = useRef(0)
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  // Initial value handles presenter remount on style reload, keeping selection in sync with the panel.
  const [selectedObstacleId, setSelectedObstacleId] = useState<string | undefined>(getActiveObstacleId)

  const showObstacles = mapLayerState.obstacles

  const handleImageLoadError = useCallback(() => {
    logger.warn('MapObstaclesLayer', 'Could not register obstacle images')
  }, [logger])

  useEffect(() => {
    requestIdRef.current += 1
    const requestId = requestIdRef.current
    let activeUrl: string | null = null

    if (!showObstacles) {
      setDataUrl(null)
      obstacleRepository.release()
      return
    }

    obstacleRepository
      .prepare()
      .then((preparedUrl) => {
        if (requestId !== requestIdRef.current || !showObstacles) {
          if (preparedUrl) obstacleRepository.release(preparedUrl)
          return
        }

        activeUrl = preparedUrl
        setDataUrl(preparedUrl)
      })
      .catch(() => setDataUrl(null))

    return () => {
      requestIdRef.current += 1
      setDataUrl(null)

      if (activeUrl) {
        obstacleRepository.release(activeUrl)
        activeUrl = null
      }
    }
  }, [obstacleRepository, showObstacles])

  // Clear local selection and panel when the layer is disabled or fails to load.
  useEffect(() => {
    if (showObstacles && dataUrl) return

    setSelectedObstacleId(undefined)

    if (FeatureInfoState.getValue().active?.type === 'obstacle') {
      FeatureInfoStateMutator.hide()
      ScreenElementsStateMutator.setFeatureInfoPanel(false)
    }
  }, [showObstacles, dataUrl])

  // Sync local selection with the panel so external close (ESC, map click, other feature panel)
  // and presenter remount on style reload converge to the panel as the source of truth.
  useEffect(() => {
    const subscription = FeatureInfoState.subscribe((state) => {
      const activeObstacleId = state.active?.type === 'obstacle' ? state.active.obstacle.obstacleId : undefined
      setSelectedObstacleId((prev) => (prev === activeObstacleId ? prev : activeObstacleId))
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleObstacleClick = useCallback(
    (obstacle: ObstacleFeatureInfoData) => {
      if (selectedObstacleId === obstacle.obstacleId) {
        setSelectedObstacleId(undefined)
        FeatureInfoStateMutator.hide()
        ScreenElementsStateMutator.setFeatureInfoPanel(false)
        return
      }

      setSelectedObstacleId(obstacle.obstacleId)

      const title = t(getObstacleTypeToken(obstacle.type) as 'MAP_OBSTACLE_INFO_FALLBACK-TITLE')
      FeatureInfoStateMutator.showObstacle(title, obstacle)
      ScreenElementsStateMutator.setFeatureInfoPanel(true)

      // Recentraliza o obstáculo na área visível útil, descontando o painel recém-aberto. Preserva o
      // zoom atual quando já estiver no nível mínimo de label (ou acima) para não afastar a câmera.
      const currentZoom = map?.getZoom?.()
      const targetZoom =
        typeof currentZoom === 'number' ? Math.max(currentZoom, MIN_OBSTACLE_LABEL_ZOOM) : MIN_OBSTACLE_LABEL_ZOOM
      MapImperativeInterface.flyToCoordinates(obstacle.coordinates, targetZoom, 500, getFeatureFlyToPadding())
    },
    [map, selectedObstacleId, t]
  )

  const handleClusterClick = useCallback((coordinates: Coordinates, expansionZoom: number) => {
    const targetZoom = Math.max(expansionZoom + OBSTACLE_CLUSTER_ZOOM_BUMP, MIN_OBSTACLE_ZOOM)
    MapImperativeInterface.flyToCoordinates(coordinates, targetZoom, 500)
  }, [])

  if (!showObstacles || !dataUrl) {
    return null
  }

  return (
    <MapObstaclesLayer
      dataUrl={dataUrl}
      selectedObstacleId={selectedObstacleId}
      onImageLoadError={handleImageLoadError}
      onObstacleClick={handleObstacleClick}
      onLabelClick={handleObstacleClick}
      onClusterClick={handleClusterClick}
    />
  )
}

export { MapObstaclesPresenter }
